<template>
  <div class="test-record-container">
    <div v-if="loading" class="loading-wrapper">
      <el-spin size="large" tip="加载中..."></el-spin>
    </div>
    
    <div v-else-if="error" class="error-wrapper">
      <el-empty description="加载失败，请稍后重试">
        <template #image>
          <i class="fa fa-exclamation-circle error-icon"></i>
        </template>
        <template #description>
          <p>{{ error }}</p>
          <el-button type="primary" @click="fetchEvaluationRecords">重新加载</el-button>
        </template>
      </el-empty>
    </div>
    
    <div v-else-if="records.length === 0" class="empty-wrapper">
      <el-empty description="暂无评测记录">
        <template #image>
          <i class="fa fa-file-text-o empty-icon"></i>
        </template>
        <template #description>
          <p>您还没有进行过志愿规划评测</p>
        </template>
      </el-empty>
    </div>
    
    <div v-else class="records-wrapper">
      <div class="header-section">
        <h2 class="title">
          <i class="fa fa-history"></i> 志愿规划评测记录
        </h2>
        <p class="subtitle">共 {{ total }} 条记录</p>
      </div>
      
      <div class="records-list">
        <div 
          v-for="record in records" 
          :key="record.id" 
          class="record-card"
          @click="viewRecord(record)"
        >
          <div class="record-header">
            <div class="record-info">
              <span class="user-name">用户：{{ record.userName }}</span>
              <!-- <span class="record-id">记录ID：{{ record.id }}</span> -->
            </div>
            <div class="record-time">
              <i class="fa fa-clock-o"></i>
              <span>{{ formatTime(record.createTime) }}</span>
            </div>
          </div>
          
          <div class="record-content">
            <div class="advice-preview">
              <i class="fa fa-file-text"></i>
              <span>{{ getPlanningAdvicePreview(record.planningAdvice) }}</span>
            </div>
          </div>
          
          <div class="record-footer">
            <el-button type="primary" size="small" @click.stop="viewRecord(record)">
              <i class="fa fa-eye"></i> 查看详情
            </el-button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 详情弹窗 -->
    <el-dialog 
      :title="'志愿规划详情'" 
      v-model="showDetail"
      width="80%"
      :before-close="closeDetail"
    >
      <div v-if="selectedRecord" class="detail-content">
        <!-- <div class="detail-info">
          <div class="info-item">
            <label>用户名称：</label>
            <span>{{ selectedRecord.userName }}</span>
          </div>
          <div class="info-item">
            <label>创建时间：</label>
            <span>{{ selectedRecord.createTime }}</span>
          </div>
          <div class="info-item">
            <label>更新时间：</label>
            <span>{{ selectedRecord.updateTime }}</span>
          </div>
        </div> -->
        
        <div class="detail-advice">
          <h3>规划建议</h3>
          <div 
            class="advice-content" 
            v-html="parsePlanningAdvice(selectedRecord.planningAdvice)"
          ></div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { getEvaluationRecords } from '@/api/surveyApi';

const loading = ref(true);
const error = ref('');
const records = ref([]);
const total = ref(0);
const showDetail = ref(false);
const selectedRecord = ref(null);

const fetchEvaluationRecords = async () => {
  loading.value = true;
  error.value = '';
  
  try {
    const response = await getEvaluationRecords();
    
    if (response.data.code === 200) {
      records.value = response.data.data.list || [];
      total.value = response.data.data.total || 0;
    } else {
      error.value = response.data.msg || '获取记录失败';
    }
  } catch (err) {
    error.value = '网络异常，请稍后重试';
    console.error('获取评测记录失败:', err);
  } finally {
    loading.value = false;
  }
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  return timeStr.replace('T', ' ').substring(0, 19);
};

const parsePlanningAdvice = (advice) => {
  if (!advice) return '<p>暂无规划建议</p>';
  
  try {
    const parsed = JSON.parse(advice);
    return parsed.planningAdvice || '<p>暂无规划建议内容</p>';
  } catch {
    return advice;
  }
};

const getPlanningAdvicePreview = (advice) => {
  if (!advice) return '暂无规划建议';
  
  try {
    const parsed = JSON.parse(advice);
    const adviceContent = parsed.planningAdvice || '';
    
    // 移除 HTML 标签获取纯文本
    const plainText = adviceContent.replace(/<[^>]*>/g, '').trim();
    
    if (!plainText) return '暂无规划建议内容';
    
    // 截取前100个字符作为预览
    if (plainText.length > 100) {
      return plainText.substring(0, 100) + '...';
    }
    
    return plainText;
  } catch {
    // 如果不是JSON格式，直接截取文本
    const plainText = advice.replace(/<[^>]*>/g, '').trim();
    if (plainText.length > 100) {
      return plainText.substring(0, 100) + '...';
    }
    return plainText || '暂无规划建议';
  }
};

const viewRecord = (record) => {
  selectedRecord.value = record;
  showDetail.value = true;
};

const closeDetail = () => {
  showDetail.value = false;
  selectedRecord.value = null;
};

onMounted(() => {
  fetchEvaluationRecords();
});
</script>

<style scoped>
.test-record-container {
  min-height: 400px;
  padding: 20px;
}

.loading-wrapper,
.error-wrapper,
.empty-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
}

.error-icon,
.empty-icon {
  font-size: 48px;
  color: #999;
}

.header-section {
  margin-bottom: 20px;
}

.title {
  font-size: 20px;
  font-weight: bold;
  color: #333;
  margin-bottom: 5px;
}

.title i {
  margin-right: 10px;
  color: #409eff;
}

.subtitle {
  color: #999;
  font-size: 14px;
}

.records-list {
  display: grid;
  gap: 16px;
}

.record-card {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.record-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.record-info {
  display: flex;
  gap: 20px;
}

.user-name,
.record-id {
  font-size: 14px;
  color: #666;
}

.record-time {
  display: flex;
  align-items: center;
  color: #999;
  font-size: 13px;
}

.record-time i {
  margin-right: 5px;
}

.record-content {
  margin-bottom: 15px;
}

.advice-preview {
  display: flex;
  align-items: center;
  color: #333;
  font-size: 14px;
}

.advice-preview i {
  margin-right: 8px;
  color: #409eff;
}

.record-footer {
  text-align: right;
}

.detail-content {
  padding: 10px;
}

.detail-info {
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.info-item {
  display: flex;
  margin-bottom: 10px;
}

.info-item label {
  width: 100px;
  font-weight: bold;
  color: #666;
}

.info-item span {
  color: #333;
}

.detail-advice h3 {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.advice-content {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  min-height: 200px;
  color: #333;
  line-height: 1.8;
}

.advice-content :deep(*) {
  max-width: 100%;
}
</style>